import React, { useEffect, useState } from 'react';
import {
    Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    Grid, MenuItem, TextField, Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useHistory } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ProjectsService } from '../../services/dao/projects-service';

const projectsService = new ProjectsService();
const item = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } };

const PROJECT_COLORS = ['#1F2F42', '#C89E57', '#10B981', '#F59E0B', '#3B82F6', '#8B5CF6', '#EF4444', '#14B8A6'];
const PROJECT_TYPES = ['personal', 'business', 'tax', 'other'];

export function BucketsScreen() {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState({ name: '', description: '', type: 'personal', color: '#1F2F42' });
    const history = useHistory();

    const load = () => {
        setLoading(true);
        projectsService.getProjects().then(r => {
            if (r?.body) setProjects(r.body);
            setLoading(false);
        });
    };

    useEffect(load, []);

    const handleCreate = async () => {
        if (!form.name.trim()) return;
        const res = await projectsService.createProject(form);
        setDialogOpen(false);
        setForm({ name: '', description: '', type: 'personal', color: '#1F2F42' });
        if (res?.body?._id) {
            history.push('/projects/' + res.body._id);
        } else {
            load();
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}><CircularProgress /></Box>;

    return (
        <Box sx={{ px: { xs: 2, md: 4 }, py: 4 }}>
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                    <div>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>Projects</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Organize your financial documents and receipts into projects
                        </Typography>
                    </div>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}
                        sx={{ borderRadius: 2, textTransform: 'none' }}>
                        New Project
                    </Button>
                </Box>
            </motion.div>

            {projects.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 10 }}>
                    <FolderIcon sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
                    <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>No projects yet</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                        Create a project to start organizing your documents.
                    </Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}
                        sx={{ borderRadius: 2, textTransform: 'none' }}>
                        Create Your First Project
                    </Button>
                </Box>
            )}

            <motion.div initial="hidden" animate="show" variants={{ show: { transition: { staggerChildren: 0.06 } } }}>
                <Grid container spacing={3}>
                    {projects.map((p) => {
                        const total = (p.findocCount || 0) + (p.receiptCount || 0);
                        const isUnassigned = !p._id;
                        const color = p.color || (isUnassigned ? '#94A3B8' : '#1F2F42');
                        return (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={p._id || 'unassigned'}>
                                <motion.div variants={item}>
                                    <Box onClick={() => history.push('/projects/' + (p._id || 'unassigned'))}
                                        sx={{
                                            p: 3, borderRadius: 3, cursor: 'pointer',
                                            background: '#fff', border: '1px solid rgba(0,0,0,0.06)',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                                            transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)',
                                            '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 12px 40px rgba(0,0,0,0.08)', borderColor: color + '40' },
                                        }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                            <Box sx={{ width: 36, height: 36, borderRadius: 2, background: color + '18',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1.5, flexShrink: 0 }}>
                                                <FolderIcon sx={{ color, fontSize: '1.3rem' }} />
                                            </Box>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem' }} noWrap>{p.name}</Typography>
                                                {p.type && !isUnassigned && (
                                                    <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'capitalize' }}>{p.type}</Typography>
                                                )}
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            {p.findocCount > 0 && (
                                                <Chip icon={<DescriptionIcon sx={{ fontSize: '0.85rem' }} />}
                                                    label={`${p.findocCount} docs`} size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />
                                            )}
                                            {p.receiptCount > 0 && (
                                                <Chip icon={<ReceiptLongIcon sx={{ fontSize: '0.85rem' }} />}
                                                    label={`${p.receiptCount} receipts`} size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />
                                            )}
                                            {total === 0 && (
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Empty</Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </motion.div>
                            </Grid>
                        );
                    })}
                </Grid>
            </motion.div>

            {/* Create project dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} PaperProps={{ sx: { borderRadius: 3, minWidth: 380 } }}>
                <DialogTitle sx={{ fontWeight: 600 }}>Create New Project</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
                    <TextField fullWidth label="Project name" value={form.name}
                        onChange={e => setForm({ ...form, name: e.target.value })}
                        autoFocus size="small"
                        onKeyDown={e => e.key === 'Enter' && handleCreate()} />
                    <TextField fullWidth label="Description (optional)" value={form.description}
                        onChange={e => setForm({ ...form, description: e.target.value })}
                        size="small" multiline rows={2} />
                    <TextField select fullWidth label="Type" value={form.type}
                        onChange={e => setForm({ ...form, type: e.target.value })} size="small">
                        {PROJECT_TYPES.map(t => <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t}</MenuItem>)}
                    </TextField>
                    <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>Color</Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {PROJECT_COLORS.map(c => (
                                <Box key={c} onClick={() => setForm({ ...form, color: c })}
                                    sx={{ width: 28, height: 28, borderRadius: '50%', background: c, cursor: 'pointer',
                                        border: form.color === c ? '3px solid #1E293B' : '3px solid transparent',
                                        transition: 'border 0.15s' }} />
                            ))}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreate} disabled={!form.name.trim()}>Create</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
